import React, { useState, useEffect } from 'react';
import { 
  Building2, Search, Filter, Users, Target, Phone, 
  ExternalLink, Eye, ChevronRight, X, Calendar, Globe, Award, Sparkles, UserCheck, Radio
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { managerAPI } from '../utils/api';

const ManagerAgencies = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bdParam = searchParams.get('bd_id');

  const [agencies, setAgencies] = useState([]);
  const [bds, setBds] = useState([]);
  const [selectedBd, setSelectedBd] = useState(bdParam || 'all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Agency Detail Modal
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyModalLoading, setAgencyModalLoading] = useState(false);
  const [agencyHosts, setAgencyHosts] = useState([]);

  // Host Center Drilldown Modal
  const [selectedHost, setSelectedHost] = useState(null);
  const [hostSessions, setHostSessions] = useState([]);
  const [loadingHostSessions, setLoadingHostSessions] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [agRes, bdRes] = await Promise.all([
          managerAPI.getAgencies(selectedBd !== 'all' ? { bd_id: selectedBd } : {}),
          managerAPI.getBDs()
        ]);
        if (agRes.data?.success) setAgencies(agRes.data.agencies || []);
        if (bdRes.data?.success) setBds(bdRes.data.bds || []);
      } catch (err) {
        console.error("Failed to load agencies:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedBd]);

  const handleOpenAgency = async (agency) => {
    setSelectedAgency(agency);
    setAgencyModalLoading(true);
    try {
      const res = await managerAPI.getAgency(agency.id);
      if (res.data?.success) {
        setSelectedAgency(res.data.agency);
        setAgencyHosts(res.data.hosts || []);
      }
    } catch (err) {
      console.error("Failed to fetch agency details:", err);
    } finally {
      setAgencyModalLoading(false);
    }
  };

  const handleOpenHostSessions = async (hostId) => {
    setLoadingHostSessions(true);
    try {
      const res = await managerAPI.getHostDetails(hostId);
      if (res.data?.success) {
        setSelectedHost(res.data.host);
        setHostSessions(res.data.sessions || []);
      }
    } catch (err) {
      console.error("Failed to fetch host details:", err);
    } finally {
      setLoadingHostSessions(false);
    }
  };

  const filteredAgencies = agencies.filter(a => {
    const term = search.toLowerCase();
    return (
      (a.agency_name && a.agency_name.toLowerCase().includes(term)) ||
      (a.agency_code && a.agency_code.toString().includes(term)) ||
      (a.real_name && a.real_name.toLowerCase().includes(term)) ||
      (a.bd_name && a.bd_name.toLowerCase().includes(term)) ||
      (a.bd_code && a.bd_code.toLowerCase().includes(term))
    );
  });

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight flex items-center gap-3">
              <Building2 className="text-purple-400" size={28} />
              <span>Managed Agencies & Commissions</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              All agencies operating within your managerial BD network with full commission breakdowns and Host Center drilldown.
            </p>
          </div>
        </div>

        {/* Filters and Search Bar */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Search agency by name, code, manager, or assigned BD..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#0b0f19]/80 border border-indigo-500/15 rounded-2xl py-4 pl-14 pr-5 text-white placeholder:text-slate-500 outline-none focus:border-indigo-500/50 text-sm"
            />
          </div>

          <div className="w-full md:w-64">
            <select
              value={selectedBd}
              onChange={(e) => setSelectedBd(e.target.value)}
              className="w-full bg-[#0b0f19]/80 border border-indigo-500/15 rounded-2xl py-4 px-5 text-white text-sm outline-none focus:border-indigo-500/50 cursor-pointer"
            >
              <option value="all">All BDs (Entire Team)</option>
              {bds.map(b => (
                <option key={b.id} value={b.id}>
                  {b.first_name} {b.last_name} ({b.user_id})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Agencies Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
          </div>
        ) : filteredAgencies.length === 0 ? (
          <div className="bg-[#0b0f19]/50 border border-indigo-500/15 rounded-3xl p-12 text-center">
            <Building2 size={48} className="mx-auto text-slate-600 mb-4" />
            <h3 className="text-lg font-bold text-white mb-1">No Agencies Found</h3>
            <p className="text-slate-400 text-sm max-w-md mx-auto">
              {search ? 'No agencies match the given search query.' : 'There are no agencies registered under your selected BD team yet.'}
            </p>
          </div>
        ) : (
          <div className="bg-[#0b0f19]/80 border border-indigo-500/15 rounded-[32px] overflow-hidden backdrop-blur-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-white/[0.01]">
                    <th className="py-4 px-6">Agency Details</th>
                    <th className="py-4 px-4">Assigned BD</th>
                    <th className="py-4 px-4">Manager / Contact</th>
                    <th className="py-4 px-3 text-center">Hosts</th>
                    <th className="py-4 px-3 text-right">Coins</th>
                    <th className="py-4 px-3 text-right">Agency Comm</th>
                    <th className="py-4 px-3 text-right">BD Comm</th>
                    <th className="py-4 px-3 text-right">Manager Comm</th>
                    <th className="py-4 px-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-sm font-medium">
                  {filteredAgencies.map((agency) => (
                    <tr key={agency.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 font-bold text-sm">
                            {agency.agency_name?.charAt(0) || 'A'}
                          </div>
                          <div>
                            <p className="font-bold text-white text-sm">{agency.agency_name}</p>
                            <p className="text-xs text-slate-400 mt-0.5">
                              Code: <span className="text-indigo-400 font-mono font-bold">{agency.agency_code}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs">
                          <UserCheck size={13} className="text-indigo-400" />
                          <span className="font-bold">{agency.bd_name || 'BD Partner'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-white text-xs font-bold">{agency.real_name}</p>
                        <p className="text-[11px] text-slate-400">{agency.whatsapp_number || 'No contact'}</p>
                      </td>
                      <td className="py-4 px-3 text-center">
                        <span className="px-2.5 py-1 rounded-lg bg-cyan-500/10 text-cyan-400 font-bold text-xs">
                          {agency.active_hosts_count || 0}
                        </span>
                      </td>
                      <td className="py-4 px-3 text-right">
                        <span className="font-bold text-amber-400 font-mono text-xs">
                          {(Number(agency.monthly_target_coins) || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="py-4 px-3 text-right font-mono text-purple-300 text-xs font-bold">
                        {(Number(agency.total_agency_commission_diamonds) || 0).toLocaleString()} 💎
                      </td>
                      <td className="py-4 px-3 text-right font-mono text-indigo-300 text-xs font-bold">
                        {(Number(agency.total_bd_commission_diamonds) || 0).toLocaleString()} 💎
                      </td>
                      <td className="py-4 px-3 text-right font-mono text-emerald-400 text-xs font-bold">
                        {(Number(agency.total_manager_commission_diamonds) || 0).toLocaleString()} 💎
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => handleOpenAgency(agency)}
                          className="px-3.5 py-2 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 text-xs font-bold flex items-center gap-1.5 ml-auto transition-all cursor-pointer"
                        >
                          <Eye size={14} />
                          <span>Drilldown</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Agency Detail Modal with Commission Breakdown & Host Inspection */}
        {selectedAgency && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0f1026] border border-indigo-500/30 rounded-[32px] p-6 md:p-8 max-w-5xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
              <button 
                onClick={() => setSelectedAgency(null)}
                className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-purple-500/20">
                  {selectedAgency.agency_name?.charAt(0) || 'A'}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedAgency.agency_name}</h3>
                  <div className="flex items-center gap-2 text-xs text-slate-400 mt-1">
                    <span>Agency Code: <b className="text-indigo-400 font-mono">#{selectedAgency.agency_code}</b></span>
                    <span>•</span>
                    <span>Assigned BD: <b className="text-purple-400">{selectedAgency.bd_name || 'BD Partner'}</b></span>
                  </div>
                </div>
              </div>

              {/* Agency Quick Info Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Country</p>
                  <p className="text-sm font-bold text-white mt-0.5">{selectedAgency.country || 'India'}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Active Hosts</p>
                  <p className="text-sm font-bold text-cyan-400 mt-0.5">{agencyHosts.length}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Agency Comm</p>
                  <p className="text-sm font-bold text-purple-400 mt-0.5 font-mono">
                    {(Number(selectedAgency.total_agency_commission_diamonds) || 0).toLocaleString()} 💎
                  </p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-emerald-300 uppercase font-bold">Manager Comm</p>
                  <p className="text-sm font-bold text-emerald-400 mt-0.5 font-mono">
                    {(Number(selectedAgency.total_manager_commission_diamonds) || 0).toLocaleString()} 💎
                  </p>
                </div>
              </div>

              {/* Hosts Table */}
              <div>
                <h4 className="text-sm font-bold text-white mb-3 flex items-center justify-between">
                  <span>Agency Hosts & Target Center ({agencyHosts.length})</span>
                </h4>

                {agencyModalLoading ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-3 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mx-auto"></div>
                    <p className="text-xs text-slate-400 mt-2">Loading agency hosts & metrics...</p>
                  </div>
                ) : agencyHosts.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-xs bg-white/[0.01] rounded-2xl">
                    No hosts registered under this agency yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-white/5 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-white/[0.02]">
                          <th className="py-3 px-4">Host</th>
                          <th className="py-3 px-3 text-center">Target Level</th>
                          <th className="py-3 px-3 text-right">Coins</th>
                          <th className="py-3 px-3 text-center">Live Days / Time</th>
                          <th className="py-3 px-3 text-right">Host Salary</th>
                          <th className="py-3 px-3 text-right">Agency Comm</th>
                          <th className="py-3 px-3 text-right">BD Comm</th>
                          <th className="py-3 px-3 text-right">Manager Comm</th>
                          <th className="py-3 px-4 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5 text-xs font-medium">
                        {agencyHosts.map((h) => {
                          const levelNum = Number(h.current_level ?? h.target_level_numeric ?? 0);
                          const validDays = Number(h.valid_days || 0);
                          const durationMins = Number(h.duration_mins || 0);
                          const durationHours = Math.floor(durationMins / 60);

                          return (
                            <tr key={h.id || h.user_id} className="hover:bg-white/[0.02] transition-colors">
                              <td className="py-3.5 px-4">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-xs overflow-hidden">
                                    {h.avatar_url ? (
                                      <img src={h.avatar_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      (h.first_name || h.user_name || 'H').charAt(0).toUpperCase()
                                    )}
                                  </div>
                                  <div>
                                    <p className="font-bold text-white text-xs">
                                      {h.first_name ? `${h.first_name} ${h.last_name || ''}`.trim() : (h.user_name || 'Host')}
                                    </p>
                                    <p className="text-[10px] text-slate-400 font-mono">
                                      ID: {h.xoxo_id || h.user_id || 'N/A'}
                                    </p>
                                  </div>
                                </div>
                              </td>

                              <td className="py-3.5 px-3 text-center">
                                <span className="px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 font-bold text-[11px] border border-indigo-500/30">
                                  Lv.{levelNum}
                                </span>
                              </td>

                              <td className="py-3.5 px-3 text-right">
                                <span className="font-bold text-amber-400 font-mono">
                                  {(Number(h.achieved_coins || h.monthly_target_coins) || 0).toLocaleString()}
                                </span>
                              </td>

                              <td className="py-3.5 px-3 text-center">
                                <div className="inline-flex flex-col items-center">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    validDays >= 10 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-300'
                                  }`}>
                                    {validDays} / 10 Days
                                  </span>
                                  <span className="text-[10px] text-slate-400 mt-0.5">
                                    {durationHours}h {durationMins % 60}m
                                  </span>
                                </div>
                              </td>

                              <td className="py-3.5 px-3 text-right font-mono font-bold text-cyan-400">
                                {(Number(h.host_salary_diamonds) || 0).toLocaleString()} 💎
                              </td>

                              <td className="py-3.5 px-3 text-right font-mono font-bold text-purple-300">
                                {(Number(h.agency_comm_diamonds) || 0).toLocaleString()} 💎
                              </td>

                              <td className="py-3.5 px-3 text-right font-mono font-bold text-indigo-300">
                                {(Number(h.bd_comm_diamonds) || 0).toLocaleString()} 💎
                              </td>

                              <td className="py-3.5 px-3 text-right font-mono font-bold text-emerald-400">
                                {(Number(h.manager_comm_diamonds) || 0).toLocaleString()} 💎
                              </td>

                              <td className="py-3.5 px-4 text-center">
                                <button
                                  onClick={() => handleOpenHostSessions(h.id || h.user_id)}
                                  className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-cyan-400 text-[11px] font-bold inline-flex items-center gap-1 cursor-pointer transition-colors"
                                >
                                  <Radio size={12} />
                                  <span>Sessions</span>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DRILLDOWN: Host Center Live Sessions Modal */}
        {selectedHost && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0b0f19] border border-cyan-500/30 rounded-[32px] p-6 md:p-8 max-w-4xl w-full shadow-2xl relative max-h-[90vh] overflow-y-auto">
              <button 
                onClick={() => setSelectedHost(null)}
                className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 font-bold text-xl overflow-hidden">
                  {selectedHost.avatar_url ? (
                    <img src={selectedHost.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (selectedHost.first_name || 'H').charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-white">
                      {selectedHost.first_name ? `${selectedHost.first_name} ${selectedHost.last_name || ''}`.trim() : (selectedHost.user_name || 'Host')}
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-mono font-bold border border-cyan-500/20">
                      ID: {selectedHost.xoxo_id || selectedHost.user_id || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Agency: <span className="text-white font-medium">{selectedHost.agency_name}</span> (#{selectedHost.agency_code}) • BD: <span className="text-purple-300 font-medium">{selectedHost.bd_name}</span>
                  </p>
                </div>
              </div>

              {/* Host Center 4 Stat Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Target Level</p>
                  <p className="text-base font-black text-indigo-400 mt-0.5">Lv.{Number(selectedHost.current_level || 0)}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Valid Days</p>
                  <p className="text-base font-black text-emerald-400 mt-0.5">{selectedHost.valid_days || 0} / 10</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Host Reward</p>
                  <p className="text-base font-black text-cyan-400 mt-0.5 font-mono">
                    {(Number(selectedHost.host_salary_diamonds) || 0).toLocaleString()} 💎
                  </p>
                </div>
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-3 text-center">
                  <p className="text-[10px] text-emerald-300 uppercase font-bold">Manager Commission</p>
                  <p className="text-base font-black text-emerald-400 mt-0.5 font-mono">
                    {(Number(selectedHost.manager_commission_diamonds) || 0).toLocaleString()} 💎
                  </p>
                </div>
              </div>

              {/* Sessions Table */}
              <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                <Radio size={16} className="text-cyan-400" />
                <span>Live Streaming Sessions History ({hostSessions.length})</span>
              </h4>

              {loadingHostSessions ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
                </div>
              ) : hostSessions.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-xs bg-white/[0.01] rounded-2xl border border-white/5">
                  No live streaming sessions recorded yet this month.
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {hostSessions.map((s, idx) => (
                    <div key={s.id || idx} className="p-3 rounded-xl bg-white/[0.02] border border-white/5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                          s.is_valid_day ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-slate-400'
                        }`}>
                          {s.is_valid_day ? '✓' : '•'}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-white">
                            {s.room_id ? `Room #${s.room_id}` : 'Live Broadcast'}
                          </p>
                          <p className="text-[10px] text-slate-400">
                            {s.created_at ? new Date(s.created_at).toLocaleString() : 'Recent'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-cyan-400">{s.duration_mins || 0} mins</p>
                        <p className="text-[10px] text-amber-400 font-mono mt-0.5">
                          +{(Number(s.coins_earned) || 0).toLocaleString()} coins
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
  );
};

export default ManagerAgencies;
